import { forwardRef } from 'react';
import * as Radix from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn';

export const Menu = Radix.Root;
export const MenuTrigger = Radix.Trigger;
export const MenuPortal = Radix.Portal;
export const MenuGroup = Radix.Group;
export const MenuRadioGroup = Radix.RadioGroup;
export const MenuSub = Radix.Sub;

export const MenuContent = forwardRef<
  React.ElementRef<typeof Radix.Content>,
  React.ComponentPropsWithoutRef<typeof Radix.Content>
>(function MenuContent({ className, sideOffset = 6, align = 'end', ...rest }, ref) {
  return (
    <Radix.Portal>
      <Radix.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[180px] rounded-md border border-border-soft bg-surface p-1 shadow-md outline-none',
          'data-[state=open]:animate-fade-in',
          className,
        )}
        {...rest}
      />
    </Radix.Portal>
  );
});

const itemBase = cn(
  'flex cursor-pointer select-none items-center gap-2 rounded-xs px-2.5 py-2 text-base text-ink',
  'transition-colors duration-fast',
  'data-[highlighted]:bg-primary-soft data-[highlighted]:text-primary',
  'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
  'focus:outline-none',
);

export const MenuItem = forwardRef<
  React.ElementRef<typeof Radix.Item>,
  React.ComponentPropsWithoutRef<typeof Radix.Item> & { destructive?: boolean }
>(function MenuItem({ className, destructive, ...rest }, ref) {
  return (
    <Radix.Item
      ref={ref}
      className={cn(itemBase, destructive && 'text-error data-[highlighted]:bg-error-soft data-[highlighted]:text-error', className)}
      {...rest}
    />
  );
});

export const MenuCheckboxItem = forwardRef<
  React.ElementRef<typeof Radix.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof Radix.CheckboxItem>
>(function MenuCheckboxItem({ className, children, ...rest }, ref) {
  return (
    <Radix.CheckboxItem
      ref={ref}
      className={cn(itemBase, 'pl-8 relative', className)}
      {...rest}
    >
      <Radix.ItemIndicator className="absolute left-2 inline-flex">
        <Check className="h-4 w-4" aria-hidden />
      </Radix.ItemIndicator>
      {children}
    </Radix.CheckboxItem>
  );
});

export const MenuRadioItem = forwardRef<
  React.ElementRef<typeof Radix.RadioItem>,
  React.ComponentPropsWithoutRef<typeof Radix.RadioItem>
>(function MenuRadioItem({ className, children, ...rest }, ref) {
  return (
    <Radix.RadioItem
      ref={ref}
      className={cn(itemBase, 'pl-8 relative', className)}
      {...rest}
    >
      <Radix.ItemIndicator className="absolute left-2 inline-flex">
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
      </Radix.ItemIndicator>
      {children}
    </Radix.RadioItem>
  );
});

export const MenuLabel = forwardRef<
  React.ElementRef<typeof Radix.Label>,
  React.ComponentPropsWithoutRef<typeof Radix.Label>
>(function MenuLabel({ className, ...rest }, ref) {
  return (
    <Radix.Label
      ref={ref}
      className={cn('px-2.5 pt-2 pb-1 text-xs font-semibold uppercase tracking-caps text-ink-muted', className)}
      {...rest}
    />
  );
});

export const MenuSeparator = forwardRef<
  React.ElementRef<typeof Radix.Separator>,
  React.ComponentPropsWithoutRef<typeof Radix.Separator>
>(function MenuSeparator({ className, ...rest }, ref) {
  return (
    <Radix.Separator
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-border-soft', className)}
      {...rest}
    />
  );
});

export const MenuSubTrigger = forwardRef<
  React.ElementRef<typeof Radix.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof Radix.SubTrigger>
>(function MenuSubTrigger({ className, children, ...rest }, ref) {
  return (
    <Radix.SubTrigger
      ref={ref}
      className={cn(itemBase, 'data-[state=open]:bg-primary-soft data-[state=open]:text-primary', className)}
      {...rest}
    >
      {children}
      <ChevronRight className="ml-auto h-4 w-4" aria-hidden />
    </Radix.SubTrigger>
  );
});

export const MenuSubContent = forwardRef<
  React.ElementRef<typeof Radix.SubContent>,
  React.ComponentPropsWithoutRef<typeof Radix.SubContent>
>(function MenuSubContent({ className, ...rest }, ref) {
  return (
    <Radix.SubContent
      ref={ref}
      className={cn(
        'z-50 min-w-[180px] rounded-md border border-border-soft bg-surface p-1 shadow-md outline-none',
        'data-[state=open]:animate-fade-in',
        className,
      )}
      {...rest}
    />
  );
});
